/**
 * Куда вернуть человека после входа (DEV-2490).
 *
 * Менеджер открывает CRM по ссылке из Telegram-бота на карточку лида. Без сессии
 * гард уводит его на форму входа с `?redirect=<путь>`, и после входа он должен
 * оказаться на той же карточке, а не на Главной.
 */

/** Ключ sessionStorage: вход через Google уходит со страницы и теряет query. */
const STORAGE_KEY = 'vp-login-redirect'

/**
 * Служебные экраны входа и сеанса: вернуть на них после входа — значит показать
 * форму, отказ или конец просмотра вместо экрана, ради которого входили.
 */
const NOT_A_DESTINATION = [
  '/login',
  '/auth/callback',
  '/no-access',
  '/impersonate',
  '/impersonation-ended',
]

/**
 * Путь, на который можно вернуть после входа, или null.
 *
 * Только путь внутри приложения: `//host` и `/\host` браузер понимает как адрес
 * чужого сайта, а `?redirect=` приходит из адресной строки и подделывается
 * кем угодно.
 */
export const safeRedirect = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value.startsWith('/') || /^\/[/\\]/.test(value)) {
    return null
  }

  const path = value.split(/[?#]/)[0] ?? ''

  // «Нет связи» — промежуточный экран: после входа ведём туда, куда человек
  // шёл до него, иначе он снова увидел бы «Нет связи» при живой сети. Query
  // берём от первого «?»: vue-router не кодирует «?» во вложенном redirect.
  if (path === '/offline') {
    const query = value.includes('?') ? value.slice(value.indexOf('?') + 1) : ''
    return safeRedirect(new URLSearchParams(query.split('#')[0]).get('redirect'))
  }

  return NOT_A_DESTINATION.includes(path) ? null : value
}

/** Запоминает путь возврата перед уходом на вход через Google. */
export const rememberLoginRedirect = (value: unknown): void => {
  const redirect = safeRedirect(value)

  try {
    if (redirect) {
      sessionStorage.setItem(STORAGE_KEY, redirect)
    } else {
      sessionStorage.removeItem(STORAGE_KEY)
    }
  } catch {
    // Хранилище закрыто (приватный режим) — вернём на Главную, вход не ломаем.
  }
}

/** Забирает запомненный путь: он одноразовый, как и сам возврат из Google. */
export const takeLoginRedirect = (): string | null => {
  try {
    const value = sessionStorage.getItem(STORAGE_KEY)
    sessionStorage.removeItem(STORAGE_KEY)

    return safeRedirect(value)
  } catch {
    return null
  }
}
